import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { CheckIcon } from '@heroicons/react/24/outline';
import { subscriptionService, Product } from '../../../services/subscriptionService';

// Using Product interface from subscriptionService

interface PricingPlansProps {
  currentSubscription?: {
    plan: {
      id: string;
    };
  } | null;
  onSelectPlan?: (priceId: string) => void;
}

const PricingPlans: React.FC<PricingPlansProps> = ({ currentSubscription, onSelectPlan }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const products = await subscriptionService.getProducts();
      setProducts(products);
      setLoading(false);
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const handleSelectPlan = (priceId: string) => {
    if (onSelectPlan) {
      onSelectPlan(priceId);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">{error}</p>
        <button
          onClick={fetchProducts}
          className={`mt-4 px-4 py-2 rounded-md ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
        >
          Try Again
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No pricing plans available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold">Choose the Right Plan for Your Team</h2>
        <p className="mt-2 text-gray-500">
          All plans include a 14-day free trial. No credit card required.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {products.map((product) => {
          const isCurrentPlan = currentSubscription?.plan?.id === product.price.id;
          const isPopular = product.metadata?.popular === 'true';

          return (
            <div
              key={product.id}
              className={`relative rounded-lg overflow-hidden flex flex-col h-full ${
                isDarkMode ? 'bg-gray-800' : 'bg-white'
              } shadow-md transition-transform hover:scale-105 ${
                isCurrentPlan ? 'ring-2 ring-blue-500' : ''
              }`}
            >
              {isPopular && (
                <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-xs font-bold uppercase">
                  Popular
                </div>
              )}

              <div className="p-6 flex-grow flex flex-col">
                <div>
                  <h3 className="text-xl font-bold">{product.name}</h3>
                  <div className="mt-4 flex items-baseline">
                    <span className="text-3xl font-extrabold">
                      {formatCurrency(product.price.unit_amount, product.price.currency)}
                    </span>
                    <span className="ml-1 text-gray-500">/{product.price.interval}</span>
                  </div>

                  {product.description && (
                    <p className="mt-4 text-sm text-gray-500">{product.description}</p>
                  )}

                  <ul className="mt-6 space-y-3">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto pt-8">
                  <button
                    onClick={() => handleSelectPlan(product.price.id)}
                    disabled={isCurrentPlan}
                    className={`w-full px-4 py-2 rounded-md ${
                      isCurrentPlan
                        ? 'bg-gray-400 cursor-not-allowed'
                        : isDarkMode
                          ? 'bg-blue-600 hover:bg-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600'
                    } text-white transition-colors`}
                  >
                    {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingPlans;
